/**
 * PositionsProcessor.js - Processeur pour la feuille "Positions (m)"
 */

import { readExcelCell } from "./excelReader.js";
import { isEmptyOrNA, findLastColumnWithValue } from "./fiberCableUtilities.js";

export class PositionsProcessor {
  constructor(workbook) {
    this.workbook = workbook;
    this.sheetName = "Positions (m)";
    this.worksheet = null;
    this.columns = null;
  }

  findSheet() {
    const errors = [];

    if (!this.workbook || !this.workbook.SheetNames) {
      errors.push("Workbook invalide");
      return { isValid: false, errors };
    }

    if (!this.workbook.SheetNames.includes(this.sheetName)) {
      errors.push(
        `Feuille "${this.sheetName}" non trouvée. Feuilles disponibles: ${this.workbook.SheetNames.join(", ")}`
      );
      return { isValid: false, errors };
    }

    this.worksheet = this.workbook.Sheets[this.sheetName];
    if (!this.worksheet) {
      errors.push(`Impossible d'accéder à la feuille "${this.sheetName}"`);
      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  }

  detectColumns() {
    const errors = [];

    if (!this.worksheet) {
      errors.push("Feuille non initialisée");
      return { isValid: false, errors };
    }

    try {
      this.columns = {
        ID_CABLE: {
          index: 1,
          letter: "B",
          name: "ID Câble",
        },
        N_FIBRE: {
          index: 2,
          letter: "C",
          name: "N° Fibre",
        },
        LAMBDA: {
          index: 4,
          letter: "E",
          name: "Lambda",
        },
        MESURES_START: 5,
      };

      this.columns.TOTAL_COLUMNS = this.detectLastColumn();

      return { isValid: true, errors: [] };
    } catch (error) {
      errors.push(`Erreur de lecture des en-têtes: ${error.message}`);
      return { isValid: false, errors };
    }
  }

  detectLastColumn() {
    for (let col = this.columns.MESURES_START; col < 100; col++) {
      const cellAddress = String.fromCharCode(65 + col) + "1";
      const cellValue = readExcelCell(this.worksheet[cellAddress]).trim();

      if (!cellValue) {
        return col; // First empty column = total columns
      }
    }

    return 50; // Fallback
  }

  extractData() {
    const errors = [];

    if (!this.worksheet || !this.columns) {
      errors.push("Feuille ou colonnes non initialisées");
      return { isValid: false, errors, data: [] };
    }

    try {
      const data = [];
      const maxRows = 10000;

      for (let row = 5; row <= maxRows + 4; row++) {
        const idCableCell = this.worksheet[`${this.columns.ID_CABLE.letter}${row}`];
        const fiberCell = this.worksheet[`${this.columns.N_FIBRE.letter}${row}`];
        const lambdaCell = this.worksheet[`${this.columns.LAMBDA.letter}${row}`];

        const idCableValue = readExcelCell(idCableCell);
        const fiberValue = readExcelCell(fiberCell);
        const lambdaValue = readExcelCell(lambdaCell);

        // Si ID Câble est vide, la feuille est terminée
        if (!idCableValue || isEmptyOrNA(idCableValue)) {
          break;
        }

        // Trouver la dernière colonne avec une valeur de mesure
        const lastCol = findLastColumnWithValue(
          this.worksheet,
          row,
          this.columns.MESURES_START,
          Math.min(100, this.columns.TOTAL_COLUMNS)
        );

        if (lastCol === -1) {
          continue;
        }

        // Lire toutes les valeurs de distance jusqu'à la dernière colonne
        const positions = [];
        for (let col = this.columns.MESURES_START; col <= lastCol; col++) {
          const positionCell = this.worksheet[`${String.fromCharCode(65 + col)}${row}`];
          const positionValue = readExcelCell(positionCell);
          const position = parseFloat(positionValue);
          if (!isNaN(position) && position > 0) {
            positions.push(position);
          }
        }

        if (positions.length === 0) {
          continue;
        }

        data.push({
          EXCEL_ROW: row,
          ID_CABLE: idCableValue.trim(),
          N_FIBRE: parseInt(fiberValue) || 0,
          LAMBDA: parseFloat(lambdaValue) || 0,
          POSITIONS: positions,
        });
      }

      return { isValid: true, errors: [], data };
    } catch (error) {
      errors.push(`Erreur d'extraction des données: ${error.message}`);
      return { isValid: false, errors, data: [] };
    }
  }

  isEndOfData(idCableValue) {
    return !idCableValue || isEmptyOrNA(idCableValue);
  }
}
